import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import './Profiles.css';

const PlateManagement = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [ranges, setRanges] = useState([]);
  const [transitos, setTransitos] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVehicleType, setFilterVehicleType] = useState('todos');
  const [filterTransitoId, setFilterTransitoId] = useState('todos');
  
  const [showRangeModal, setShowRangeModal] = useState(false);
  const [rangeForm, setRangeForm] = useState({
    transito_id: '',
    prefix: '',
    start_number: '',
    end_number: '',
    vehicle_type: 'carro',
    suffix: ''
  });

  const [concesionarios, setConcesionarios] = useState([]);
  const [selectedConcesionarioId, setSelectedConcesionarioId] = useState('');
  const [loadingStats, setLoadingStats] = useState(false);

  const isSuperAdmin = user?.role === 'superadmin' || user?.profile_name === 'superadmin';

  // Pre-cargar el transito_id si es admin de un tránsito
  useEffect(() => {
    if (showRangeModal && (user?.role === 'admin' || user?.role === 'admin_transito' || user?.profile_name === 'admin_transito') && user?.transito_id) {
      setRangeForm(prev => ({ ...prev, transito_id: user.transito_id }));
    }
  }, [showRangeModal, user]);

  useEffect(() => {
    const isAllowed = isSuperAdmin || 
                      ['admin_transito', 'admin_concesionario', 'admin concesionario', 'admin_punto_venta', 'concesionario', 'asesor'].includes(user?.profile_name) || 
                      ['admin_transito', 'admin_concesionario', 'admin_punto_venta', 'admin', 'concesionario', 'asesor'].includes(user?.role) ||
                      !!user?.concesionario_id || !!user?.transito_id;
    if (!isAllowed) {
      navigate('/dashboard');
    }
    fetchData();
  }, [user, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchData(false);
      if (selectedConcesionarioId) {
        handleFilterStatsByConcesionario(selectedConcesionarioId);
      }
    }, 30000); // Refresco en vivo cada 30 segundos

    return () => clearInterval(interval);
  }, [selectedConcesionarioId]);

  const fetchData = async (resetStats = true) => {
    try {
      setLoading(true);
      const calls = [
        axios.get('/api/transitos/ranges'),
        axios.get('/api/transitos')
      ];

      // Para concesionarios normales, cargar sus stats inmediatamente
      if (!isSuperAdmin) {
        calls.push(axios.get('/api/transitos/stats/plates'));
      } else {
        // Para superadmin, cargar lista de concesionarios para permitir filtro a demanda
        calls.push(axios.get('/api/concesionarios'));
      }

      const [rangesRes, transitosRes, thirdRes] = await Promise.all(calls);
      setRanges(rangesRes.data);
      setTransitos(transitosRes.data);

      if (!isSuperAdmin) {
        setStats(thirdRes.data);
      } else {
        setConcesionarios(thirdRes.data);
        if (resetStats && !selectedConcesionarioId) {
          setStats([]); // En superadmin inicia limpio para no sobrecargar
        }
      }
      setError('');
    } catch (err) {
      setError('Error al cargar datos de placas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterStatsByConcesionario = async (concId) => {
    setSelectedConcesionarioId(concId);
    if (!concId) {
      setStats([]);
      return;
    }
    try {
      setLoadingStats(true);
      const statsRes = await axios.get(`/api/transitos/stats/plates?concesionario_id=${concId}`);
      setStats(statsRes.data || []);
    } catch (err) {
      console.error('Error al filtrar stats por concesionario:', err);
      setStats([]);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleSaveRange = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/transitos/ranges', rangeForm);
      fetchData();
      setShowRangeModal(false);
      setRangeForm({ transito_id: '', prefix: '', start_number: '', end_number: '', vehicle_type: 'carro', suffix: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear rango');
    }
  };

  const handleDeleteRange = async (range) => {
    const confirmMessage = `¿Estás seguro de que deseas eliminar el rango de placas ${range.prefix} (${range.start_number} - ${range.end_number})? \n\nEsta acción borrará todas las placas asociadas que estén disponibles y devolverá cualquier solicitud a estado PENDIENTE.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      setLoading(true);
      await axios.delete(`/api/transitos/ranges/${range.id}`);
      alert('✅ Rango de placas eliminado correctamente.');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al eliminar rango de placas');
      console.error(err);
      setLoading(false);
    }
  };

  const filteredRanges = ranges.filter(r => {
    const term = searchTerm.toLowerCase().trim();
    const matchesSearch = 
      !term ||
      r.prefix?.toLowerCase().includes(term) ||
      (r.suffix && r.suffix.toLowerCase().includes(term)) ||
      r.transito_name?.toLowerCase().includes(term) ||
      r.vehicle_type?.toLowerCase().includes(term) ||
      String(r.start_number).includes(term) ||
      String(r.end_number).includes(term);

    const matchesType = filterVehicleType === 'todos' || r.vehicle_type === filterVehicleType;
    const matchesTransito = filterTransitoId === 'todos' || String(r.transito_id) === String(filterTransitoId);

    return matchesSearch && matchesType && matchesTransito;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterVehicleType, filterTransitoId]);

  // Lógica de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRanges.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Navbar title="🆔 Gestión y Control de Placas" />

        <div className="page-content">
          {user?.profile_name === 'administrativo_concesionario' && (localStorage.getItem('concesionario_filtro') || 'todos') === 'todos' && (
            <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', color: '#92400e', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontWeight: 600 }}>
              ⚠️ Para ver las placas seleccione un concesionario en el filtro de la parte superior.
            </div>
          )}
          {error && <div className="error-banner">{error}</div>}

          {/* Filtro a Demanda para Superadmin por Concesionario */}
          {(user?.role === 'superadmin' || user?.profile_name === 'superadmin') && (
            <div style={{ background: '#fff', padding: '16px 20px', borderRadius: '12px', border: '1px solid var(--border-color)', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>🏢</span>
                <div>
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>Consulta de Inventario de Placas por Concesionario</h4>
                  <small style={{ color: 'var(--text-secondary)' }}>Seleccione un concesionario para consultar su disponibilidad.</small>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <select
                  value={selectedConcesionarioId}
                  onChange={(e) => handleFilterStatsByConcesionario(e.target.value)}
                  style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff', minWidth: '260px' }}
                >
                  <option value="">-- Seleccionar Concesionario --</option>
                  {isSuperAdmin && (
                    <option value="transiapp">⚡ TransiApp (Placas Libres Globales)</option>
                  )}
                  {concesionarios.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {loadingStats && <span style={{ fontSize: '13px', color: 'var(--primary-color)' }}>Cargando...</span>}
              </div>
            </div>
          )}

          {stats.length > 0 && (
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
              {Object.entries(
                stats.reduce((acc, s) => {
                  if (!acc[s.transito_name]) {
                    acc[s.transito_name] = [];
                  }
                  acc[s.transito_name].push(s);
                  return acc;
                }, {})
              ).map(([transitoName, items], idx) => {
                const totalLibres = items.reduce((sum, item) => sum + (item.disponibles || 0), 0);
                const totalTransiApp = items.reduce((sum, item) => sum + (item.asignadas_transiapp || 0), 0);
                const totalGeneral = items.reduce((sum, item) => sum + (item.total || 0), 0);
                return (
                  <div key={idx} className="stat-card info" style={{ padding: '16px 20px', display: 'block' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                      <span style={{ fontSize: '20px' }}>🏛️</span>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>{transitoName}</h4>
                        <small style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>
                          Total Tránsito: {totalLibres} Libres {totalTransiApp > 0 ? `(${totalTransiApp} TransiApp)` : ''} / {totalGeneral} Total
                        </small>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {items.map((it, itemIdx) => (
                        <div key={itemIdx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', padding: '6px 10px', borderRadius: '6px', fontSize: '13px' }}>
                          <span style={{ fontWeight: '600', color: '#334155' }}>
                            {it.vehicle_type === 'moto' ? '🏍️ Moto' : it.vehicle_type === 'moto_carro' ? '🛺 Moto Carro' : '🚗 Carro'}:
                          </span>
                          <span style={{ color: '#0f172a' }}>
                            <strong>{it.disponibles}</strong> libres
                            {it.asignadas_transiapp !== undefined && (
                              <span style={{ color: '#0284c7', fontSize: '12px', marginLeft: '5px', fontWeight: '600' }}>
                                ({it.asignadas_transiapp} TransiApp)
                              </span>
                            )}
                            {' '}| <strong>{it.consumidas || 0}</strong> usadas
                            {it.pre_asignadas > 0 && <small style={{ color: '#d97706', marginLeft: '4px' }}>({it.pre_asignadas} pre-asig)</small>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="page-header" style={{ flexWrap: 'wrap', gap: '15px' }}>
            <div className="header-title">
              <h2>{user?.concesionario_id ? 'Rangos de Placas Asignados al Concesionario' : 'Rangos de Placas Asignados'}</h2>
              <p className="subtitle">
                {user?.concesionario_id 
                  ? 'Consulta los rangos asignados y el historial completo de placas usadas' 
                  : 'Configura los prefijos y números disponibles para cada tránsito'}
              </p>
            </div>
            <div className="filters" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="🔍 Buscar por prefijo, tránsito, número..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', minWidth: '240px' }}
              />
              <select 
                value={filterVehicleType} 
                onChange={(e) => setFilterVehicleType(e.target.value)}
                style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff' }}
              >
                <option value="todos">Todos los vehículos</option>
                <option value="carro">🚗 Carro</option>
                <option value="moto">🏍️ Moto</option>
                <option value="moto_carro">🛺 Moto Carro</option>
              </select>
              {!user?.concesionario_id && transitos.length > 0 && (
                <select
                  value={filterTransitoId}
                  onChange={(e) => setFilterTransitoId(e.target.value)}
                  style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff', minWidth: '180px' }}
                >
                  <option value="todos">🏛️ Todos los tránsitos</option>
                  {transitos.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              )}
              {user?.concesionario_id && (
                <button 
                  onClick={() => navigate('/plates/inventory/all')} 
                  className="btn-primary"
                  style={{ background: '#10b981', borderColor: '#059669', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  👁️ Ver Historial y Control de Placas
                </button>
              )}
              {(user?.role === 'superadmin' || user?.profile_name === 'superadmin' || (user?.transito_id && (user?.role === 'admin' || user?.role === 'admin_transito' || user?.profile_name === 'admin_transito'))) && (
                <button onClick={() => setShowRangeModal(true)} className="btn-primary">
                  ➕ Asignar Rango
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <p className="loading">Cargando rangos...</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="users-table">
                <thead>
                  <tr>
                    {!user?.concesionario_id && <th>Tránsito</th>}
                    <th>Tipo</th>
                    <th>Prefijo</th>
                    <th>Inicio</th>
                    <th>Fin</th>
                    <th>Actual</th>
                    <th>Disponibles</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(r => (
                    <tr key={r.id}>
                      {!user?.concesionario_id && <td className="user-name">{r.transito_name}</td>}
                      <td>
                        <span className={`vehicle-type-badge ${r.vehicle_type}`}>
                          {r.vehicle_type === 'moto' ? '🏍️ Moto' : 
                           r.vehicle_type === 'moto_carro' ? '🛺 Moto Carro' : 
                           '🚗 Carro'}
                        </span>
                      </td>
                      <td>
                        <strong>{r.prefix}</strong>
                        {r.suffix && <span style={{ color: '#64748b', marginLeft: '4px' }}>- {r.suffix}</span>}
                      </td>
                      <td>{r.start_number}</td>
                      <td>{r.end_number}</td>
                      <td>{r.current_number}</td>
                      <td>
                        <span className="plate-badge-sm" style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', fontWeight: '800' }}>
                          {r.disponibles}
                        </span>
                      </td>
                      <td>
                        <span className={`status ${r.is_active ? 'activo' : 'inactivo'}`}>
                          {r.is_active ? 'Activo' : 'Finalizado'}
                        </span>
                      </td>
                      <td className="actions" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button 
                          onClick={() => navigate(`/plates/inventory/${r.transito_id}?range_id=${r.id}`)} 
                          className="btn-action btn-view"
                          title="Ver Inventario Detallado"
                        >
                          👁️ Detalle
                        </button>
                        {user?.role === 'superadmin' && (
                          <button 
                            onClick={() => handleDeleteRange(r)} 
                            className="btn-action btn-delete"
                            title="Eliminar Rango de Placas"
                          >
                            🗑️ Eliminar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              {filteredRanges.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No se encontraron rangos de placas con los criterios de búsqueda.
                </div>
              )}

              <Pagination 
                currentPage={currentPage}
                totalItems={filteredRanges.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </div>

      {showRangeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Asignar Nuevo Rango de Placas</h3>
            <form onSubmit={handleSaveRange}>
              {user?.role === 'superadmin' ? (
                <div className="form-group">
                  <label>Seleccionar Tránsito</label>
                  <select 
                    value={rangeForm.transito_id} 
                    onChange={(e) => setRangeForm({...rangeForm, transito_id: e.target.value})}
                    required
                  >
                    <option value="">Seleccione un tránsito...</option>
                    {transitos.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>Tránsito</label>
                  <input 
                    type="text" 
                    value={transitos.find(t => t.id === parseInt(user?.transito_id))?.name || 'Cargando...'} 
                    disabled 
                    style={{ background: '#f1f5f9', fontWeight: 'bold' }}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Tipo de Vehículo</label>
                <select 
                  value={rangeForm.vehicle_type} 
                  onChange={(e) => setRangeForm({...rangeForm, vehicle_type: e.target.value})}
                  required
                >
                  <option value="carro">🚗 Carro (Letras + Números)</option>
                  <option value="moto">🏍️ Moto (Letras + Números + Letra)</option>
                  <option value="moto_carro">🛺 Moto Carro (Números + Letras)</option>
                  <option value="otro">📦 Otro</option>
                </select>
              </div>

              <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
                <small style={{ color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '5px' }}>
                  Estructura detectada:
                </small>
                {rangeForm.vehicle_type === 'moto' ? (
                  <span style={{ fontSize: '13px', color: '#2563eb' }}>
                    <strong>3 Letras</strong> (Prefijo) + <strong>2 Números</strong> + <strong>1 Letra</strong> (Sufijo)
                  </span>
                ) : rangeForm.vehicle_type === 'moto_carro' ? (
                  <span style={{ fontSize: '13px', color: '#2563eb' }}>
                    <strong>3 Números</strong> + <strong>3 Letras</strong> (Prefijo)
                  </span>
                ) : (
                  <span style={{ fontSize: '13px', color: '#2563eb' }}>
                    <strong>3 Letras</strong> (Prefijo) + <strong>3 Números</strong>
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>{rangeForm.vehicle_type === 'moto_carro' ? 'Letras Finales' : 'Prefijo de Letras'}</label>
                <input
                  type="text"
                  maxLength="3"
                  placeholder="Ej: ABC"
                  value={rangeForm.prefix}
                  onChange={(e) => setRangeForm({...rangeForm, prefix: e.target.value.toUpperCase()})}
                  required
                />
              </div>

              {rangeForm.vehicle_type === 'moto' && (
                <div className="form-group">
                  <label>Letra Final (Sufijo)</label>
                  <input
                    type="text"
                    maxLength="1"
                    placeholder="Ej: A"
                    value={rangeForm.suffix}
                    onChange={(e) => setRangeForm({...rangeForm, suffix: e.target.value.toUpperCase()})}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label>Número Inicial</label>
                <input
                  type="number"
                  min="0"
                  max={rangeForm.vehicle_type === 'moto' ? "99" : "999"}
                  placeholder={rangeForm.vehicle_type === 'moto' ? "00" : "000"}
                  value={rangeForm.start_number}
                  onChange={(e) => setRangeForm({...rangeForm, start_number: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Número Final</label>
                <input
                  type="number"
                  min="0"
                  max={rangeForm.vehicle_type === 'moto' ? "99" : "999"}
                  placeholder={rangeForm.vehicle_type === 'moto' ? "99" : "999"}
                  value={rangeForm.end_number}
                  onChange={(e) => setRangeForm({...rangeForm, end_number: e.target.value})}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowRangeModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Generar Placas
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlateManagement;
